import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { APP_CONFIG } from '@/src/config/app.config';
import { CoindeskHistoricalResponse, CoindeskQueryKeys } from './coindesk.types';

interface UseCoindeskHistoricalParams {
  instrument?: string;
  days?: number;
  enabled?: boolean;
}

interface CandlestickData {
  time: number;
  date: string;
  openClose: [number, number]; // [open, close]
  high: number;
  low: number;
  volume: number;
}

/**
 * Get historical daily candlestick data from CoinDesk API
 * @param instrument Trading pair (e.g., "XRP-USD")
 * @param days Number of days to fetch (default: 20)
 */
export const useCoindeskHistorical = ({
  instrument = APP_CONFIG.charts.xrpUsdInstrument,
  days = APP_CONFIG.charts.defaultDays,
  enabled = true,
}: UseCoindeskHistoricalParams = {}) => {
  return useQuery({
    queryKey: [CoindeskQueryKeys.HistoricalDays, instrument, days],
    queryFn: async () => {
      const response = await axios.get<CoindeskHistoricalResponse>(
        `${APP_CONFIG.coindesk.baseUrl}/historical/days`,
        {
          params: {
            instrument,
            limit: days,
            aggregate: 1,
            fill: true,
            apply_mapping: true,
            response_format: 'JSON',
            market: APP_CONFIG.charts.market,
            api_key: APP_CONFIG.coindesk.apiKey,
          },
        }
      );

      // Transform to candlestick format
      const candlesticks: CandlestickData[] = response.data.Data.map((candle) => {
        return {
          time: candle.TIMESTAMP,
          date: new Date(candle.TIMESTAMP * 1000).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
          openClose: [candle.OPEN, candle.CLOSE],
          high: candle.HIGH,
          low: candle.LOW,
          volume: candle.VOLUME,
        };
      });

      // Sort by time (oldest first)
      candlesticks.sort((a, b) => a.time - b.time);

      return candlesticks;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};
